import React, { useState, useEffect } from 'react';
import {
  ShieldCheck,
  Plus,
  Search,
  Edit2,
  Trash2,
  AlertCircle,
  Loader2,
  X,
  Layers,
  Eye,
  Download,
  FileText,
  Paperclip,
} from 'lucide-react';
import { controlApi, usersApi } from '../api';
import { toast } from 'sonner';
import { extractApiError, validateDateFields, formatDateBR } from '../utils/dateValidation';

const STATUS_LABELS = {
  regular: { label: 'Regular', color: 'bg-green-500/20 text-green-400' },
  a_vencer: { label: 'A Vencer', color: 'bg-yellow-500/20 text-yellow-400' },
  vencido: { label: 'Vencido', color: 'bg-red-500/20 text-red-400' },
};

// "Controle" is group-based, like Documentos Externos: the user creates a
// group (e.g. Dedetização, Lavagem de Caixa D'Água) with its own prazo de
// vencimento, and every group shares the same standard form. Each filled
// record can be viewed/downloaded as a PDF.
const Controle = () => {
  const [groups, setGroups] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [stats, setStats] = useState({ total: 0, overdue: 0, due_soon: 0, regular: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [users, setUsers] = useState([]);
  const [pdfBusyId, setPdfBusyId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  useEffect(() => {
    loadGroups();
    loadUsers();
  }, []);

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId, overdueOnly]);

  const loadGroups = async () => {
    try {
      const res = await controlApi.listGroups();
      setGroups(res.data || []);
      if (res.data?.length > 0 && !selectedGroupId) {
        setSelectedGroupId(res.data[0].id);
      }
    } catch (error) {
      toast.error('Erro ao carregar grupos');
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ limit: 100 });
      setUsers(res.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadRecords = async () => {
    if (!selectedGroupId) {
      setRecords([]);
      setStats({ total: 0, overdue: 0, due_soon: 0, regular: 0 });
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = { group_id: selectedGroupId, overdue_only: overdueOnly || undefined };
      const [recordsRes, statsRes] = await Promise.all([
        controlApi.listRecords(params),
        controlApi.stats({ group_id: selectedGroupId }),
      ]);
      setRecords(recordsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar registros');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (data) => {
    try {
      await controlApi.createGroup(data);
      toast.success('Grupo criado com sucesso');
      setShowGroupModal(false);
      await loadGroups();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar grupo'));
    }
  };

  const handleUpdateGroup = async (data) => {
    try {
      await controlApi.updateGroup(editingGroup.id, data);
      toast.success('Grupo atualizado com sucesso');
      setShowGroupModal(false);
      setEditingGroup(null);
      await loadGroups();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar grupo'));
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Excluir o grupo "${group.name}"? Os registros do grupo também serão removidos.`)) return;
    try {
      await controlApi.deleteGroup(group.id);
      toast.success('Grupo excluído');
      setSelectedGroupId('');
      await loadGroups();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao excluir grupo'));
    }
  };

  const handleCreateRecord = async (data) => {
    try {
      await controlApi.createRecord({ ...data, group_id: selectedGroupId });
      toast.success('Registro criado com sucesso');
      setShowRecordModal(false);
      await Promise.all([loadRecords(), loadGroups()]);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar registro'));
    }
  };

  const handleUpdateRecord = async (data) => {
    try {
      await controlApi.updateRecord(editingRecord.id, data);
      toast.success('Registro atualizado com sucesso');
      setShowRecordModal(false);
      setEditingRecord(null);
      loadRecords();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar'));
    }
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro?')) return;
    try {
      await controlApi.deleteRecord(id);
      toast.success('Registro excluído');
      await Promise.all([loadRecords(), loadGroups()]);
    } catch (error) {
      toast.error('Erro ao excluir registro');
    }
  };

  const handleViewPdf = async (record) => {
    setPdfBusyId(`view-${record.id}`);
    try {
      const res = await controlApi.getRecordPdf(record.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error('Erro ao visualizar PDF');
    } finally {
      setPdfBusyId(null);
    }
  };

  const handleDownloadPdf = async (record) => {
    setPdfBusyId(`download-${record.id}`);
    try {
      const res = await controlApi.getRecordPdf(record.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${record.code}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar PDF');
    } finally {
      setPdfBusyId(null);
    }
  };

  const handleUploadFile = async (record, file) => {
    if (!file) return;
    setUploadingId(record.id);
    try {
      await controlApi.uploadFile(record.id, file);
      toast.success('Documento anexado com sucesso');
      loadRecords();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao anexar documento'));
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteFile = async (record) => {
    setUploadingId(record.id);
    try {
      await controlApi.deleteFile(record.id);
      toast.success('Anexo removido');
      loadRecords();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao remover anexo'));
    } finally {
      setUploadingId(null);
    }
  };

  const handleDownloadFile = async (record) => {
    try {
      const res = await controlApi.downloadFile(record.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', record.file_name || `anexo_${record.code}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar anexo');
    }
  };

  const filteredRecords = records.filter((r) =>
    !searchTerm ||
    r.area?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.provider?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedGroup = groups.find((g) => g.id === selectedGroupId);

  return (
    <div className="space-y-6" data-testid="controle-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <ShieldCheck className="text-green-500" />
            Controle
          </h1>
          <p className="text-gray-400 mt-1">Grupos de controle com prazo de vencimento e formulário padrão</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setEditingGroup(null); setShowGroupModal(true); }} className="btn-secondary flex items-center gap-2">
            <Plus size={20} />
            Novo Grupo
          </button>
          <button
            onClick={() => { setEditingRecord(null); setShowRecordModal(true); }}
            className="btn-primary flex items-center gap-2"
            disabled={!selectedGroupId}
            data-testid="add-controle-record-btn"
          >
            <Plus size={20} />
            Novo Registro
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Total de Registros</p>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Vencidos</p>
          <p className="text-2xl font-bold text-red-400">{stats.overdue}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">A Vencer (30 dias)</p>
          <p className="text-2xl font-bold text-yellow-400">{stats.due_soon}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Grupos</p>
          <p className="text-2xl font-bold text-white">{groups.length}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Groups sidebar */}
        <div className="card overflow-hidden lg:col-span-1">
          <div className="p-3 border-b border-gray-700 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-300 flex items-center gap-2">
              <Layers size={16} className="text-green-500" />
              Grupos
            </span>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {groups.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 text-center">Nenhum grupo criado</p>
            ) : (
              groups.map((group) => (
                <div
                  key={group.id}
                  onClick={() => setSelectedGroupId(group.id)}
                  className={`flex items-center justify-between px-3 py-3 cursor-pointer border-b border-gray-800 transition-colors ${
                    selectedGroupId === group.id ? 'bg-green-500/10 border-l-2 border-l-green-500' : 'hover:bg-gray-800/40'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{group.name}</p>
                    {group.description && (
                      <p className="text-xs text-gray-500 truncate">{group.description}</p>
                    )}
                    <p className="text-xs text-gray-500">Prazo: a cada {group.frequency_days} dias · {group.records_count} registro(s)</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => { setEditingGroup(group); setShowGroupModal(true); }}
                      className="p-1 text-gray-400 hover:text-blue-400"
                      title="Editar grupo"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group)}
                      className="p-1 text-gray-400 hover:text-red-400"
                      title="Excluir grupo"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Records table */}
        <div className="card overflow-hidden lg:col-span-3">
          <div className="p-3 border-b border-gray-700 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Buscar por área, código ou empresa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>
            <label className="flex items-center gap-2 text-gray-400">
              <input
                type="checkbox"
                checked={overdueOnly}
                onChange={(e) => setOverdueOnly(e.target.checked)}
                className="form-checkbox text-green-500"
              />
              Apenas Vencidos
            </label>
          </div>

          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Área / Local</th>
                  <th>Empresa</th>
                  <th>Última Execução</th>
                  <th>Status</th>
                  <th>Próxima Data</th>
                  <th>Anexo</th>
                  <th>PDF</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="py-12 text-center">
                      <Loader2 className="animate-spin text-green-500 inline" size={28} />
                    </td>
                  </tr>
                ) : filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="py-12 text-center text-gray-500">
                      {selectedGroupId ? 'Nenhum registro encontrado neste grupo' : 'Selecione ou crie um grupo para ver os registros'}
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((record) => (
                    <tr key={record.id}>
                      <td className="font-mono text-white">{record.code}</td>
                      <td className="text-white font-medium">{record.area}</td>
                      <td className="text-gray-400">{record.provider || '-'}</td>
                      <td className="text-gray-400">{formatDateBR(record.service_date)}</td>
                      <td>
                        <span className={`px-2 py-1 rounded-full text-xs ${STATUS_LABELS[record.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                          {STATUS_LABELS[record.status]?.label || record.status}
                        </span>
                      </td>
                      <td>
                        <span className={record.status === 'vencido' ? 'text-red-400 font-semibold' : 'text-gray-400'}>
                          {formatDateBR(record.next_date)}
                          {record.status === 'vencido' && <AlertCircle size={14} className="inline ml-1" />}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {record.has_file ? (
                            <>
                              <button
                                onClick={() => handleDownloadFile(record)}
                                className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                                title={`Baixar anexo: ${record.file_name}`}
                              >
                                <Download size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteFile(record)}
                                disabled={uploadingId === record.id}
                                className="text-red-400 hover:text-red-300"
                                title="Remover anexo"
                              >
                                {uploadingId === record.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                              </button>
                            </>
                          ) : (
                            <label
                              className="flex items-center gap-1 text-xs text-gray-400 hover:text-white cursor-pointer"
                              title="Anexar documento (será incorporado ao PDF)"
                            >
                              {uploadingId === record.id ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
                              Anexar
                              <input
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg"
                                style={{ display: 'none' }}
                                disabled={uploadingId === record.id}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  e.target.value = '';
                                  if (f) handleUploadFile(record, f);
                                }}
                              />
                            </label>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewPdf(record)}
                            disabled={pdfBusyId === `view-${record.id}`}
                            className="text-blue-400 hover:text-blue-300"
                            title="Visualizar PDF"
                          >
                            {pdfBusyId === `view-${record.id}` ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
                          </button>
                          <button
                            onClick={() => handleDownloadPdf(record)}
                            disabled={pdfBusyId === `download-${record.id}`}
                            className="text-gray-400 hover:text-white"
                            title="Baixar PDF"
                          >
                            {pdfBusyId === `download-${record.id}` ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                          </button>
                        </div>
                      </td>
                      <td>
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => { setEditingRecord(record); setShowRecordModal(true); }} className="p-1 text-gray-400 hover:text-blue-400">
                            <Edit2 size={18} />
                          </button>
                          <button onClick={() => handleDeleteRecord(record.id)} className="p-1 text-gray-400 hover:text-red-400">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Group Modal */}
      {showGroupModal && (
        <GroupModal
          group={editingGroup}
          onClose={() => { setShowGroupModal(false); setEditingGroup(null); }}
          onSave={editingGroup ? handleUpdateGroup : handleCreateGroup}
        />
      )}

      {/* Record Modal */}
      {showRecordModal && (
        <RecordModal
          record={editingRecord}
          group={selectedGroup}
          users={users}
          onClose={() => { setShowRecordModal(false); setEditingRecord(null); }}
          onSave={editingRecord ? handleUpdateRecord : handleCreateRecord}
        />
      )}
    </div>
  );
};

const GroupModal = ({ group, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: group?.name || '',
    description: group?.description || '',
    frequency_days: group?.frequency_days || 90,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...formData, frequency_days: parseInt(formData.frequency_days) || 90 };
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">{group ? 'Editar Grupo' : 'Novo Grupo'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input w-full"
              placeholder="Ex: Dedetização, Lavagem de Caixa D'Água"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full"
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Prazo de Vencimento (dias) *</label>
            <input
              type="number"
              min="1"
              value={formData.frequency_days}
              onChange={(e) => setFormData({ ...formData, frequency_days: e.target.value })}
              className="input w-full"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Periodicidade padrão usada para calcular a próxima data de vencimento dos registros deste grupo.</p>
          </div>
          <div className="flex items-start gap-2 p-3 rounded-lg border border-gray-700 bg-gray-800/40">
            <FileText size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-400">
              Todo grupo usa o mesmo formulário padrão: Área/Local, Data do Serviço, Frequência, Próxima Data,
              Produto Utilizado, Registro do Produto, Empresa Terceirizada, Técnico Responsável, Nº Certificado,
              Responsável Interno e Observações.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={20} /> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const RecordModal = ({ record, group, users, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    area: record?.area || '',
    service_date: record?.service_date || new Date().toISOString().split('T')[0],
    frequency_days: record?.frequency_days || group?.frequency_days || 90,
    next_date: record?.next_date || '',
    product_used: record?.product_used || '',
    registration_number: record?.registration_number || '',
    provider: record?.provider || '',
    technician: record?.technician || '',
    certificate_number: record?.certificate_number || '',
    responsible_id: record?.responsible_id || '',
    observations: record?.observations || '',
    status: record?.status || 'regular',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dateError = validateDateFields([
      { value: formData.service_date, label: 'Data do Serviço' },
      { value: formData.next_date, label: 'Próxima Data' },
    ]);
    if (dateError) { toast.error(dateError); return; }
    setSaving(true);
    try {
      const data = { ...formData };
      data.frequency_days = data.frequency_days ? parseInt(data.frequency_days) : null;
      if (!data.next_date) delete data.next_date;
      if (!data.responsible_id) delete data.responsible_id;
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {record ? 'Editar Registro' : `Novo Registro${group ? ` de ${group.name}` : ''}`}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Área / Local *</label>
              <input
                type="text"
                value={formData.area}
                onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                className="input w-full"
                placeholder="Ex: Depósito, Cozinha, Caixa D'Água - Cobertura"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Data do Serviço *</label>
              <input
                type="date"
                value={formData.service_date}
                onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Frequência (dias)</label>
              <input
                type="number"
                value={formData.frequency_days}
                onChange={(e) => setFormData({ ...formData, frequency_days: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Próxima Data</label>
              <input
                type="date"
                value={formData.next_date}
                onChange={(e) => setFormData({ ...formData, next_date: e.target.value })}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
                placeholder="Calculado automaticamente se vazio"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Produto Utilizado</label>
              <input
                type="text"
                value={formData.product_used}
                onChange={(e) => setFormData({ ...formData, product_used: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Registro do Produto</label>
              <input
                type="text"
                value={formData.registration_number}
                onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Empresa Terceirizada</label>
              <input
                type="text"
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Técnico Responsável</label>
              <input
                type="text"
                value={formData.technician}
                onChange={(e) => setFormData({ ...formData, technician: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nº Certificado</label>
              <input
                type="text"
                value={formData.certificate_number}
                onChange={(e) => setFormData({ ...formData, certificate_number: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Responsável Interno</label>
              <select
                value={formData.responsible_id}
                onChange={(e) => setFormData({ ...formData, responsible_id: e.target.value })}
                className="input w-full"
              >
                <option value="">Selecione...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.full_name}</option>
                ))}
              </select>
            </div>
            {record && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="input w-full"
                >
                  <option value="regular">Regular</option>
                  <option value="a_vencer">A Vencer</option>
                  <option value="vencido">Vencido</option>
                </select>
              </div>
            )}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Observações</label>
              <textarea
                value={formData.observations}
                onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                className="input w-full"
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={20} /> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Controle;
